import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  TicketCheck,
  Mail,
  Lock,
  User,
  Phone,
  Eye,
  EyeOff,
  MessageCircle,
  Gift,
  LogIn,
  UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';

const RENEWAL_WHATSAPP = '5492262619878';

const HomePage = () => {
  const navigate = useNavigate();

  // Login state
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);

  // Register state
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
  });
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setSubscriptionExpired(false);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      localStorage.setItem('admin_token', response.data.access_token);
      toast.success('¡Bienvenido!');
      window.location.href = '/admin';
    } catch (error) {
      if (error.response?.data?.detail === 'SUBSCRIPTION_EXPIRED') {
        setSubscriptionExpired(true);
      } else {
        toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.name || !registerData.email || !registerData.phone || !registerData.password) {
      toast.error('Completá todos los campos');
      return;
    }
    if (registerData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setRegisterLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, registerData);
      localStorage.setItem('admin_token', response.data.access_token);
      toast.success(`¡Cuenta creada! Tenés ${response.data.trial_days} días de prueba gratis 🎉`);
      setRegisterOpen(false);
      window.location.href = '/admin';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrarse');
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleContactForInfo = () => {
    const message = encodeURIComponent(
      'Hola! Me gustaría recibir información sobre Sorteo Fácil para usar la aplicación en mi comercio.'
    );
    window.location.href = `https://api.whatsapp.com/send?phone=${RENEWAL_WHATSAPP}&text=${message}`;
  };

  const handleRenewal = () => {
    const message = encodeURIComponent(
      `Hola! Mi período de prueba venció. Me gustaría contratar una suscripción de Sorteo Fácil. Mi email es: ${loginData.email}`
    );
    window.location.href = `https://api.whatsapp.com/send?phone=${RENEWAL_WHATSAPP}&text=${message}`;
  };

  if (subscriptionExpired) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 via-blue-50 to-indigo-100 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <Lock size={32} className="text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-3">Período de prueba expirado</h2>
          <p className="text-slate-600 mb-6 leading-relaxed">
            Tu período de prueba gratuito ha finalizado. Tus rifas y sorteos activos siguen visibles
            para tus clientes, pero no podés acceder al panel hasta contratar una suscripción.
          </p>
          <Button
            onClick={handleRenewal}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-6 text-lg rounded-xl shadow-lg"
            data-testid="expired-renewal-button"
          >
            <MessageCircle className="mr-2" size={22} />
            Contratar suscripción por WhatsApp
          </Button>
          <button
            onClick={() => setSubscriptionExpired(false)}
            className="mt-5 text-sm text-slate-500 hover:text-slate-800"
          >
            Volver al login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-blue-50 to-indigo-100 flex flex-col">
      {/* Decorative glows */}
      <div className="pointer-events-none absolute -top-24 -left-24 w-96 h-96 rounded-full bg-sky-200/50 blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 -right-24 w-96 h-96 rounded-full bg-indigo-200/40 blur-3xl" />

      {/* Main login area */}
      <main className="flex-1 flex items-center justify-center px-4 py-3 relative z-10">
        <div className="w-full max-w-md">
          {/* Branding */}
          <div className="text-center mb-3">
            <div
              className="mx-auto mb-2 inline-flex items-center justify-center w-14 h-14 rounded-2xl shadow-lg"
              style={{
                background:
                  'linear-gradient(135deg, #1E88E5 0%, #1565C0 45%, #2E7D32 100%)',
              }}
            >
              <TicketCheck size={32} strokeWidth={2.3} className="text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight" data-testid="home-title">
              Sorteo Fácil
            </h1>
            <p className="mt-0.5 text-xs sm:text-sm text-slate-600">
              Organizá mejor, vendé más.
            </p>
          </div>

          {/* Login Card */}
          <div className="bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl border border-white/60 p-4 sm:p-5">
            <h2 className="text-base font-bold text-slate-900 mb-3">Iniciá sesión</h2>
            <form onSubmit={handleLogin} className="space-y-2.5">
              <div>
                <Label htmlFor="login-email" className="text-slate-700 text-xs">Email</Label>
                <div className="relative mt-0.5">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    id="login-email"
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    placeholder="tu@email.com"
                    className="pl-9 h-9 text-sm"
                    required
                    data-testid="home-login-email"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="login-password" className="text-slate-700 text-xs">Contraseña</Label>
                <div className="relative mt-0.5">
                  <Lock className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    id="login-password"
                    type={showLoginPassword ? 'text' : 'password'}
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    placeholder="••••••••"
                    className="pl-9 pr-9 h-9 text-sm"
                    required
                    data-testid="home-login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                    className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-700"
                    tabIndex={-1}
                  >
                    {showLoginPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-xs text-blue-600 hover:underline"
                  data-testid="home-forgot-password-link"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-700 hover:bg-blue-800 text-white font-semibold h-10 rounded-xl text-sm"
                disabled={loginLoading}
                data-testid="home-login-submit"
              >
                <LogIn className="mr-2" size={16} />
                {loginLoading ? 'Ingresando...' : 'Iniciar sesión'}
              </Button>
            </form>

            {/* Separator */}
            <div className="relative my-3">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-[10px] uppercase tracking-wider">
                <span className="bg-white px-2 text-slate-400">o</span>
              </div>
            </div>

            {/* Trial register CTA */}
            <div className="text-center">
              <p className="text-xs text-slate-600 mb-2">
                ¿Todavía no tenés cuenta?
              </p>
              <Button
                onClick={() => setRegisterOpen(true)}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold h-10 rounded-xl text-sm shadow-md"
                data-testid="home-open-register"
              >
                <Gift className="mr-2" size={16} />
                Probá gratis 5 días
              </Button>
              <p className="text-[10px] text-slate-500 mt-1.5">
                Sin tarjeta · Sin compromiso · Acceso inmediato
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Bottom prominent CTA */}
      <section className="relative z-10 px-4 pb-3">
        <div className="max-w-md mx-auto">
          <div className="relative rounded-2xl p-3.5 text-center shadow-xl overflow-hidden"
               style={{
                 background: 'linear-gradient(135deg, #0F172A 0%, #1E3A8A 100%)',
               }}>
            <div className="pointer-events-none absolute -top-10 -right-10 w-32 h-32 rounded-full bg-emerald-500/20 blur-3xl" />
            <h3 className="text-sm sm:text-base font-extrabold text-white mb-1">
              ¿Querés usar la app en tu comercio?
            </h3>
            <p className="text-slate-300 text-[11px] mb-2.5">
              Escribinos y te contamos cómo potenciar tus rifas y sorteos.
            </p>
            <Button
              onClick={handleContactForInfo}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold h-9 rounded-xl text-sm shadow-lg hover:shadow-emerald-500/40 transition-all"
              data-testid="home-contact-info-button"
            >
              <MessageCircle className="mr-2" size={16} />
              Contactanos por WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-center text-[10px] text-slate-500 pb-2 relative z-10">
        © {new Date().getFullYear()} Sorteo Fácil
      </footer>

      {/* Register Dialog */}
      <Dialog open={registerOpen} onOpenChange={setRegisterOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl">Probá gratis 5 días</DialogTitle>
            <DialogDescription>
              Creá tu cuenta y accedé al panel al instante. Sin tarjeta.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRegister} className="space-y-4 mt-2">
            <div>
              <Label htmlFor="reg-name">Nombre completo</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="reg-name"
                  value={registerData.name}
                  onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                  placeholder="Tu nombre y apellido"
                  className="pl-10"
                  required
                  data-testid="home-register-name"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="reg-email">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="reg-email"
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  placeholder="tu@email.com"
                  className="pl-10"
                  required
                  data-testid="home-register-email"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="reg-phone">Teléfono (WhatsApp)</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="reg-phone"
                  type="tel"
                  value={registerData.phone}
                  onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                  placeholder="2262-619878"
                  className="pl-10"
                  required
                  data-testid="home-register-phone"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="reg-password">Contraseña</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="reg-password"
                  type={showRegisterPassword ? 'text' : 'password'}
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  placeholder="Mínimo 6 caracteres"
                  className="pl-10 pr-10"
                  required
                  minLength={6}
                  data-testid="home-register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                  className="absolute right-3 top-3 text-slate-400 hover:text-slate-700"
                  tabIndex={-1}
                >
                  {showRegisterPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 flex items-start gap-2">
              <Gift size={16} className="mt-0.5 flex-shrink-0" />
              <span>Al registrarte recibís <strong>5 días de prueba gratis</strong> con todas las funciones.</span>
            </div>

            <DialogFooter className="sm:justify-stretch">
              <Button
                type="submit"
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-6 rounded-xl text-base"
                disabled={registerLoading}
                data-testid="home-register-submit"
              >
                <UserPlus className="mr-2" size={18} />
                {registerLoading ? 'Creando cuenta...' : 'Crear cuenta y empezar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePage;
